import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Button, Flex, Modal, Stack, Text } from '@mantine/core';

import { useGetBoardUsersInfinite, usePostBoardQueueGroup } from 'api/boards';
import {
  CheckboxField,
  DescriptionField,
  Form,
  SelectField,
  TextField,
  UserAvatar,
} from 'components/shared';
import { useShowNotification } from 'hooks';

type Props = {
  opened: boolean;
  onClose: () => void;
};

export const QueueGroupCreateModal = ({ opened, onClose }: Props) => {
  const { organisationId, boardId } = useParams();
  if (!organisationId || !boardId) throw new Error('Missing URL params');

  const { data: boardUsers } = useGetBoardUsersInfinite({ boardId });

  const [requiredApprovalsOptions, setRequiredApprovalsOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const form = useForm({
    defaultValues: {
      name: '',
      boardUsersMap: {},
      requiredApprovals: '1',
    },
  });

  const {
    formState: { isSubmitting },
    setValue,
    watch,
  } = form;

  const { mutateAsync: postBoardQueueGroup } = usePostBoardQueueGroup();
  const showNotification = useShowNotification();
  const { t } = useTranslation('organisations');

  const boardUsersMapValue = watch('boardUsersMap');

  const resetFormBoardUsersMap = () => {
    if (boardUsers) {
      const boardUsersMap = boardUsers.reduce(
        (acc, user) => {
          acc[user.boardUserId] = false;
          return acc;
        },
        {} as Record<string, boolean>,
      );
      setValue('boardUsersMap', boardUsersMap);
    }
  };

  useEffect(() => {
    resetFormBoardUsersMap();
  }, [boardUsers]);

  useEffect(() => {
    if (boardUsersMapValue) {
      const maxRequiredApprovals =
        Object.entries(boardUsersMapValue).filter(([_, value]) => value)
          .length - 1;

      if (Number(watch('requiredApprovals')) > maxRequiredApprovals)
        setValue('requiredApprovals', maxRequiredApprovals.toString());

      setRequiredApprovalsOptions(
        Array.from({ length: maxRequiredApprovals }, (_, i) => ({
          label: (i + 1).toString(),
          value: (i + 1).toString(),
        })),
      );
    }
  }, [JSON.stringify(boardUsersMapValue)]);

  const onSubmit = async ({ boardUsersMap, ...rest }: any) => {
    try {
      await postBoardQueueGroup({
        pathParams: { organisationId, boardId },
        body: {
          boardUsers: Object.entries(boardUsersMap).flatMap(
            ([boardUserId, value]) => (value ? [{ id: boardUserId }] : []),
          ),
          ...rest,
        },
      });
      showNotification({
        message: t(
          'organisationView.boards.queueGroups.createModal.successMessage',
        ),
        variant: 'success',
      });
      onClose();
      form.reset();
      resetFormBoardUsersMap();
    } catch (error) {
      showNotification({
        message: t(
          'organisationView.boards.queueGroups.createModal.errorMessage',
        ),
        variant: 'error',
      });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      title={t('organisationView.boards.queueGroups.createModal.title')}
    >
      <Form form={form} onSubmit={onSubmit}>
        <Stack py={24} sx={{ gap: 24 }}>
          <DescriptionField
            label={t(
              'organisationView.boards.queueGroups.createModal.fields.name.label',
            )}
            description={t(
              'organisationView.boards.queueGroups.createModal.fields.name.description',
            )}
            error={undefined}
          >
            <TextField
              name="name"
              placeholder={t(
                'organisationView.boards.queueGroups.createModal.fields.name.label',
              )}
              withErrorWrapperAlwaysEnabled={false}
            />
          </DescriptionField>
          <DescriptionField
            label={t(
              'organisationView.boards.queueGroups.createModal.fields.members.label',
            )}
            error={undefined}
          >
            <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
              {boardUsers?.map(user => (
                <CheckboxField
                  key={user.boardUserId}
                  name={`boardUsersMap.${user.boardUserId}`}
                  label={
                    <Flex
                      align="center"
                      gap="lg"
                      sx={theme => ({
                        marginBottom: theme.other.spacing(2),
                        '&:last-child': { marginBottom: 0 },
                      })}
                    >
                      <UserAvatar
                        name={user.name}
                        surname={user.surname}
                        avatar={user.avatarUrl}
                      />
                      <Text sx={{ flex: 1 }}>
                        {user.name && user.surname
                          ? `${user.name} ${user.surname}`
                          : user.email}
                      </Text>
                    </Flex>
                  }
                  styles={theme => ({
                    body: {
                      border: `1px solid ${theme.colors.gray[1]}`,
                      borderRadius: theme.radius.sm,
                      paddingRight: theme.other.spacing(1.5),
                      '&:has(input:checked)': {
                        borderColor: theme.colors.violet[0],
                        backgroundColor: theme.colors.violet[1],
                      },
                    },
                    labelWrapper: { order: 1, flexGrow: 1 },
                    label: { padding: theme.other.spacing(1) },
                    inner: { order: 2 },
                  })}
                  sx={{
                    '&:last-child': { marginBottom: 0 },
                  }}
                />
              ))}
            </Box>
          </DescriptionField>
          <DescriptionField
            label={t(
              'organisationView.boards.queueGroups.createModal.fields.requiredApprovals.label',
            )}
            description={t(
              'organisationView.boards.queueGroups.createModal.fields.requiredApprovals.description',
            )}
            error={undefined}
          >
            <SelectField
              name="requiredApprovals"
              label={t(
                'organisationView.boards.queueGroups.createModal.fields.requiredApprovals.selectLabel',
              )}
              data={requiredApprovalsOptions}
            />
          </DescriptionField>
        </Stack>
        <Flex justify="flex-end" gap={16}>
          <Button variant="default" onClick={onClose}>
            {t('organisationView.boards.queueGroups.createModal.cancelButton')}
          </Button>
          <Button color="green" type="submit" loading={isSubmitting}>
            {t('organisationView.boards.queueGroups.createModal.createButton')}
          </Button>
        </Flex>
      </Form>
    </Modal>
  );
};
